import { useEffect, useState } from 'react';
import Logo from '../components/Logo';
import { clearSignedOutFlag } from '../contexts/AuthContext';
import { loadCatalystSdk } from '../utils/catalystSdk';

// US-F03 — Staff sign-in via the Catalyst Web SDK embedded widget.
// The widget handles the OAuth dance with Zoho / social providers and,
// on success, redirects the browser to service_url (our SPA root) with
// a Catalyst session cookie set. AuthContext's finalize-on-mount hook
// then calls POST /auth/sso/finalize to exchange that for a portal
// session.

const WIDGET_ELEMENT_ID = 'staff-signin-container';

export default function StaffLogin() {
  const [error, setError] = useState(null);

  useEffect(() => {
    // 2026-05-08 — user navigated to a sign-in surface, so any prior
    // explicit signed-out intent is now stale. Clear so finalize-on-success
    // can re-issue a portal session.
    clearSignedOutFlag();
    let cancelled = false;
    (async () => {
      try {
        const catalyst = await loadCatalystSdk();
        if (cancelled) return;
        if (typeof catalyst.auth.signIn !== 'function') {
          throw new Error('Catalyst Web SDK did not initialise');
        }
        // 2026-05-22: Zoho OAuth client returning API119 on both Dev + Prod
        // (open Zoho support ticket). Removed signin_providers_only flag
        // entirely so the Catalyst widget renders the full Login Form
        // (email + password) alongside enabled social providers. Setting
        // the flag to `false` was NOT equivalent — SDK v4 checks
        // `typeof config.signin_providers_only !== 'undefined'`, so any
        // explicit value (true OR false) routes to the social-only widget
        // shape per Catalyst Console → Authentication → Login Form sample.
        //
        // Once Zoho support restores the OAuth client, restore the original
        // `signin_providers_only: true` to lock the widget down to SSO-only.
        // 2026-05-26 — Catalyst iframe is cross-origin, so host CSS cannot
        // style its interior. SDK accepts `css_url` (sign-in) +
        // `forgot_password_css_url` (password reset) options pointing at
        // files hosted on the host page's static origin; the SDK injects
        // a <link> with that URL into the iframe document, applying the
        // overrides inside the cross-origin frame. CSS files live at
        // app/public/css/ and are served at /app/css/<filename> after the
        // CRA build is deployed to Catalyst. Mirrors the sibling
        // owner-portal/client wiring.
        const cssOrigin = window.location.origin;
        catalyst.auth.signIn(WIDGET_ELEMENT_ID, {
          service_url: `${window.location.origin}/app/index.html`,
          css_url: `${cssOrigin}/app/css/embeddediframe.css`,
          is_customize_forgot_password: true,
          forgot_password_css_url: `${cssOrigin}/app/css/embedded_password_reset.css`
        });
      } catch (e) {
        if (!cancelled) setError('Unable to load Catalyst sign-in. Refresh and try again.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={styles.page}>
      {/*
        2026-05-15 — kill the inner scrollbar the Catalyst SDK injects.
        The SDK builds its provider list inside a fixed-height container
        with overflow:auto; outer-div styles alone don't suppress it,
        so we target every descendant with !important via a scoped
        <style> tag. Plain inline styles can't reach into SDK-injected
        descendants, hence the global selector.
      */}
      {/*
        2026-05-26 — kill the inner Catalyst iframe scrollbar.
        The SDK injects an <iframe> with a hard-coded height (~280px)
        and its provider list overflows, producing an inner scrollbar.
        `overflow: visible` on the iframe element does NOT make it grow —
        only an explicit `height` does. Mirrors the sibling
        owner-portal/client wiring (height: 520px on the iframe element).
        The width:calc(100% + 20px) + margin-left:-10px is the sibling's
        trick to absorb the iframe's internal padding so content aligns
        flush with the card edges.
      */}
      <style>{`
        #${WIDGET_ELEMENT_ID} {
          overflow: hidden;
          width: 100%;
        }
        #${WIDGET_ELEMENT_ID} iframe {
          width: calc(100% + 20px) !important;
          height: 520px !important;
          border: none !important;
          display: block !important;
          margin-left: -10px;
        }
      `}</style>
      <div style={styles.card}>
        <div style={styles.brandRow}>
          <Logo width={180} style={{ margin: '0 auto' }} />
          <div style={styles.sub}>Owner Portal · Staff</div>
        </div>

        <h1 style={styles.title}>Staff sign-in</h1>
        <p style={styles.subtitle}>Use your Zoho One or social-provider account to sign in.</p>

        {error && <div style={styles.error} role="alert">{error}</div>}

        <div id={WIDGET_ELEMENT_ID} style={styles.widget} />

        <div style={styles.footer}>
          <a href="#/login" style={styles.link}>Owner sign-in</a>
          <span style={styles.divider}>·</span>
          <a href="#/help" style={styles.link}>Need help?</a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--cream)', padding: '20px' },
  card: { backgroundColor: 'var(--white)', borderRadius: '12px', padding: '48px 44px', width: '100%', maxWidth: '480px', boxShadow: 'var(--shadow-card)', border: '1px solid var(--gray-100)' },
  brandRow: { textAlign: 'center', marginBottom: '28px' },
  sub: { fontSize: '10px', color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.14em', marginTop: '6px', fontWeight: '500' },
  title: { fontSize: '22px', fontWeight: '700', color: 'var(--navy)', margin: '0 0 6px 0', textAlign: 'center', letterSpacing: '-0.01em' },
  subtitle: { fontSize: '14px', color: 'var(--gray-600)', margin: '0 0 24px 0', textAlign: 'center' },
  error: { backgroundColor: 'var(--red-light)', border: '1px solid var(--red)', borderRadius: '8px', padding: '11px 14px', fontSize: '13px', color: 'var(--red)', marginBottom: '16px' },
  widget: {
    width: '100%',
    display: 'block'
  },
  footer: { marginTop: '22px', textAlign: 'center', fontSize: '13px', color: 'var(--gray-600)' },
  link: { color: 'var(--teal)', textDecoration: 'none' },
  divider: { margin: '0 10px', color: 'var(--gray-200)' }
};
